import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { Plus, X, FileSpreadsheet, Award, ShoppingCart, ChevronRight, Pencil, Lock, Unlock, Check, BarChart3, SplitSquareHorizontal, AlertTriangle, Send, Ban } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700",
  sent: "bg-blue-100 text-blue-800",
  quoted: "bg-amber-100 text-amber-800",
  awarded: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function RFQManagement() {
  const navigate = useNavigate();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ style_number: "", techpack_id: "", target_quantity: 0, currency: "USD", response_deadline: "", required_lead_time_weeks: "", bom_summary: "" });
  const [saving, setSaving] = useState(false);
  const [techpacks, setTechpacks] = useState([]);
  const [showAddQuote, setShowAddQuote] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierMap, setSupplierMap] = useState({});
  const [quoteForm, setQuoteForm] = useState({ supplier_id: "", lead_time_weeks: "", moq: "", payment_terms: "", price_tiers: "" });
  const [editingQuote, setEditingQuote] = useState(null);
  const [editQuoteForm, setEditQuoteForm] = useState({});
  const [comparison, setComparison] = useState(null);
  const [showSplitAward, setShowSplitAward] = useState(false);
  const [splitAwards, setSplitAwards] = useState([]);

  const load = () => {
    Promise.all([
      api.get("/rfqs").then((r) => setRfqs(r.data)).catch(() => {}),
      api.get("/techpacks").then((r) => setTechpacks(r.data)).catch(() => {}),
      api.get("/suppliers").then((r) => { setSuppliers(r.data); const m = {}; r.data.forEach((s) => { m[s.id] = s.company_name; }); setSupplierMap(m); }).catch(() => {}),
    ]).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const loadDetail = (rfq) => {
    api.get(`/rfqs/${rfq.id}`).then((r) => setSelected(r.data)).catch(() => setSelected(rfq));
  };

  const create = async () => {
    setSaving(true);
    try {
      const bomItems = form.bom_summary ? form.bom_summary.split(",").map((s) => s.trim()).filter(Boolean).map((s) => ({ item: s })) : [];
      await api.post("/rfqs", {
        style_number: form.style_number,
        techpack_id: form.techpack_id || undefined,
        target_quantity: parseInt(form.target_quantity, 10) || 0,
        required_lead_time_weeks: form.required_lead_time_weeks ? parseInt(form.required_lead_time_weeks, 10) : null,
        bom_summary: bomItems,
        currency: form.currency,
        response_deadline: form.response_deadline ? new Date(form.response_deadline).toISOString() : undefined,
      });
      toast.success("RFQ created");
      setShowCreate(false);
      setForm({ style_number: "", techpack_id: "", target_quantity: 0, currency: "USD", response_deadline: "", required_lead_time_weeks: "", bom_summary: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addQuotation = async (rfqId) => {
    try {
      const tiers = quoteForm.price_tiers ? quoteForm.price_tiers.split(",").map((p) => p.trim()).filter(Boolean).map((p) => ({ quantity_threshold: 1, unit_price: parseFloat(p) })) : [];
      await api.post(`/rfqs/${rfqId}/quotations`, {
        supplier_id: quoteForm.supplier_id,
        price_tiers: tiers,
        lead_time_weeks: parseInt(quoteForm.lead_time_weeks, 10) || null,
        moq: parseInt(quoteForm.moq, 10) || null,
        payment_terms: quoteForm.payment_terms,
      });
      toast.success("Quotation added");
      setShowAddQuote(null);
      setQuoteForm({ supplier_id: "", lead_time_weeks: "", moq: "", payment_terms: "", price_tiers: "" });
      load();
      if (selected) loadDetail(selected);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // The step the chain was missing: an award now becomes an order without retyping its terms.
  const createPOFromAward = async (rfqId) => {
    try {
      const { data } = await api.post(`/rfqs/${rfqId}/create-purchase-order`);
      toast.success(`Purchase order ${data.po_number} created`);
      navigate(`/purchase-orders/${data.id}`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const awardQuotation = async (rfqId, quotationId) => {
    try {
      await api.post(`/rfqs/${rfqId}/award`, { quotation_id: quotationId });
      toast.success("Quotation awarded");
      load();
      setSelected(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const startEditQuote = (q) => {
    setEditingQuote(q.id);
    setEditQuoteForm({
      price_tiers: (q.price_tiers || []).map((t) => t.unit_price).join(", "),
      lead_time_weeks: q.lead_time_weeks || "",
      moq: q.moq || "",
      payment_terms: q.payment_terms || "",
    });
  };

  const saveEditQuote = async (quotationId) => {
    try {
      const tiers = editQuoteForm.price_tiers ? editQuoteForm.price_tiers.split(",").map((p) => p.trim()).filter(Boolean).map((p) => ({ quantity_threshold: 1, unit_price: parseFloat(p) })) : undefined;
      await api.patch(`/rfq-quotations/${quotationId}`, {
        price_tiers: tiers,
        lead_time_weeks: editQuoteForm.lead_time_weeks ? parseInt(editQuoteForm.lead_time_weeks, 10) : null,
        moq: editQuoteForm.moq ? parseInt(editQuoteForm.moq, 10) : null,
        payment_terms: editQuoteForm.payment_terms || "",
      });
      toast.success("Quotation updated");
      setEditingQuote(null);
      if (selected) loadDetail(selected);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleLockQuote = async (quotationId, currentLocked) => {
    try {
      await api.patch(`/rfq-quotations/${quotationId}`, { locked: !currentLocked });
      toast.success(currentLocked ? "Quotation unlocked" : "Quotation locked");
      if (selected) loadDetail(selected);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendRfq = async (rfqId) => {
    try {
      await api.patch(`/rfqs/${rfqId}`, { status: "sent" });
      toast.success("RFQ sent to suppliers");
      load();
      setSelected(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const cancelRfq = async (rfqId) => {
    try {
      await api.patch(`/rfqs/${rfqId}`, { status: "cancelled" });
      toast.success("RFQ cancelled");
      load();
      setSelected(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const getDeadlineWarning = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const dl = new Date(deadline);
    const diffMs = dl - now;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);
    if (diffDays < 0) return "past";
    if (diffDays <= 3) return "soon";
    return null;
  };

  const loadComparison = async (rfqId) => {
    try {
      const r = await api.get(`/rfqs/${rfqId}/comparison`);
      setComparison(r.data);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const openSplitAward = () => {
    if (!comparison || !comparison.matrix) return;
    setSplitAwards(
      comparison.matrix.map((m) => ({
        quotation_id: m.quotation_id,
        supplier_name: m.supplier_name,
        percentage: 0,
        quantity: 0,
      }))
    );
    setShowSplitAward(true);
  };

  const updateSplitAward = (idx, field, value) => {
    setSplitAwards((prev) =>
      prev.map((a, i) => (i === idx ? { ...a, [field]: value } : a))
    );
  };

  const submitSplitAward = async () => {
    if (!selected) return;
    const active = splitAwards.filter((a) => a.percentage > 0);
    if (active.length === 0) {
      toast.error("Assign percentages to at least one supplier");
      return;
    }
    const totalPct = active.reduce((s, a) => s + parseFloat(a.percentage || 0), 0);
    if (Math.abs(totalPct - 100) > 0.01) {
      toast.error(`Percentages must sum to 100 (currently ${totalPct})`);
      return;
    }
    try {
      await api.post(`/rfqs/${selected.id}/split-award`, {
        awards: active.map((a) => ({
          quotation_id: a.quotation_id,
          percentage: parseFloat(a.percentage),
          quantity: parseInt(a.quantity, 10) || 0,
        })),
      });
      toast.success("Split award applied");
      setShowSplitAward(false);
      setComparison(null);
      load();
      setSelected(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">RFQ Management</h1>
          <p className="text-zinc-500 mt-2 text-sm">Create requests for quotation, compare supplier quotes and award orders.</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
          <Plus size={15} /> New RFQ
        </button>
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : rfqs.length === 0 ? (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
          <FileSpreadsheet size={26} className="mx-auto mb-3" />
          <p className="text-sm">No RFQs yet.</p>
        </div>
      ) : (
        <div className="mt-8">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Total RFQs</p>
              <p className="text-2xl font-bold mt-1">{rfqs.length}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Draft</p>
              <p className="text-2xl font-bold mt-1">{rfqs.filter((r) => r.status === "draft").length}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Sent / Active</p>
              <p className="text-2xl font-bold mt-1">{rfqs.filter((r) => r.status === "sent" || r.status === "quoted").length}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Awarded</p>
              <p className="text-2xl font-bold mt-1">{rfqs.filter((r) => r.status === "awarded").length}</p>
            </div>
          </div>

          {/* RFQ List */}
          <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
            <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
              <span className="col-span-4">Style</span>
              <span className="col-span-2">Deadline</span>
              <span className="col-span-2">Target Qty</span>
              <span className="col-span-2">Status</span>
              <span className="col-span-2 text-right">Action</span>
            </div>
            {rfqs.map((r) => (
              <div key={r.id} className="grid grid-cols-12 items-center px-6 py-4 border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer"
                onClick={() => loadDetail(r)}>
                <span className="col-span-4 text-sm font-medium">{r.style_number || r.id.slice(0, 8)}</span>
                <span className="col-span-2 text-sm text-zinc-600 flex items-center gap-1">
                  {r.response_deadline?.slice(0, 10) || "N/A"}
                  {getDeadlineWarning(r.response_deadline) === "past" && <AlertTriangle size={13} className="text-red-500 shrink-0" />}
                  {getDeadlineWarning(r.response_deadline) === "soon" && <AlertTriangle size={13} className="text-amber-500 shrink-0" />}
                </span>
                <span className="col-span-2 text-sm text-zinc-600">Qty: {r.target_quantity || 0}{r.required_lead_time_weeks ? ` / ${r.required_lead_time_weeks}wk` : ""}</span>
                <span className="col-span-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[r.status] || STATUS_STYLE.draft}`}>{r.status}</span>
                  {r.status === "awarded" && (
                    <button
                      onClick={(e) => { e.stopPropagation(); createPOFromAward(r.id); }}
                      title="Raise a purchase order from the awarded quotation"
                      className="ml-2 inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded bg-zinc-900 text-white hover:bg-zinc-700"
                    >
                      <ShoppingCart size={11} /> Create PO
                    </button>
                  )}
                </span>
                <span className="col-span-2 text-right"><ChevronRight size={14} className="inline text-zinc-400" /></span>
              </div>
            ))}
          </div>

          {/* Quotation Comparison */}
          {selected && (
            <div className="mt-8 border border-zinc-200 bg-white rounded-lg p-6">
              {/* RFQ Details */}
              {selected.required_lead_time_weeks && (
                <p className="text-xs text-zinc-500 mb-2">Required lead time: <span className="font-medium text-zinc-700">{selected.required_lead_time_weeks} weeks</span></p>
              )}
              {selected.bom_summary && selected.bom_summary.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-[0.15em] text-zinc-400 mb-1">BOM Summary</p>
                  <div className="flex flex-wrap gap-1">
                    {selected.bom_summary.map((b, i) => (
                      <span key={i} className="text-[10px] bg-zinc-100 text-zinc-600 px-2 py-0.5 rounded">{b.item || b}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-semibold">Quotations — {selected.style_number || selected.id?.slice(0, 8)}</h3>
                <div className="flex gap-2">
                  {selected.status === "draft" && (
                    <button onClick={() => sendRfq(selected.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-md text-xs font-medium hover:bg-blue-700">
                      <Send size={12} /> Send to Suppliers
                    </button>
                  )}
                  {selected.status !== "awarded" && selected.status !== "cancelled" && (
                    <button onClick={() => cancelRfq(selected.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 rounded-md text-xs font-medium hover:bg-red-100">
                      <Ban size={12} /> Cancel
                    </button>
                  )}
                  <button onClick={() => setShowAddQuote(selected.id)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-950 text-white rounded-md text-xs font-medium hover:bg-zinc-800">
                    <Plus size={12} /> Add Quote
                  </button>
                  {selected.quotations && selected.quotations.length >= 2 && selected.status !== "awarded" && (
                    <button onClick={() => loadComparison(selected.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-800 rounded-md text-xs font-medium hover:bg-blue-100">
                      <BarChart3 size={12} /> Compare
                    </button>
                  )}
                  {comparison && comparison.matrix && comparison.matrix.length >= 2 && selected.status !== "awarded" && (
                    <button onClick={openSplitAward}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-800 rounded-md text-xs font-medium hover:bg-amber-100">
                      <SplitSquareHorizontal size={12} /> Split Award
                    </button>
                  )}
                  <button onClick={() => { setSelected(null); setComparison(null); }} className="text-xs text-zinc-500 hover:text-zinc-800">Close</button>
                </div>
              </div>
              {selected.quotations && selected.quotations.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-200">
                        <th className="text-left py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-normal">Supplier</th>
                        <th className="text-left py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-normal">Price Tiers</th>
                        <th className="text-left py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-normal">MOQ</th>
                        <th className="text-left py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-normal">Lead Time</th>
                        <th className="text-left py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-normal">Terms</th>
                        <th className="text-left py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-normal">Status</th>
                        <th className="text-right py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-normal">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selected.quotations.map((q) => (
                        <tr key={q.id} className="border-b border-zinc-50">
                          <td className="py-3 font-medium">{supplierMap[q.supplier_id] || q.supplier_id?.slice(0, 8)}</td>
                          <td className="py-3 text-xs">
                            {editingQuote === q.id ? (
                              <input value={editQuoteForm.price_tiers} onChange={(e) => setEditQuoteForm({ ...editQuoteForm, price_tiers: e.target.value })}
                                className="border border-zinc-300 rounded px-2 py-1 text-xs w-28" placeholder="prices" />
                            ) : (q.price_tiers || []).map((t) => `$${t.unit_price}`).join(", ") || "—"}
                          </td>
                          <td className="py-3">
                            {editingQuote === q.id ? (
                              <input type="number" value={editQuoteForm.moq} onChange={(e) => setEditQuoteForm({ ...editQuoteForm, moq: e.target.value })}
                                className="border border-zinc-300 rounded px-2 py-1 text-xs w-16" />
                            ) : q.moq || "—"}
                          </td>
                          <td className="py-3">
                            {editingQuote === q.id ? (
                              <input type="number" value={editQuoteForm.lead_time_weeks} onChange={(e) => setEditQuoteForm({ ...editQuoteForm, lead_time_weeks: e.target.value })}
                                className="border border-zinc-300 rounded px-2 py-1 text-xs w-16" />
                            ) : q.lead_time_weeks ? `${q.lead_time_weeks} wks` : "—"}
                          </td>
                          <td className="py-3 text-xs text-zinc-600">{q.payment_terms || "—"}</td>
                          <td className="py-3">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[q.status] || STATUS_STYLE.draft}`}>{q.status}</span>
                            {q.locked && <Lock size={10} className="inline ml-1 text-zinc-400" />}
                          </td>
                          <td className="py-3 text-right flex items-center justify-end gap-1">
                            {editingQuote === q.id ? (
                              <button onClick={() => saveEditQuote(q.id)}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-800 rounded text-xs hover:bg-emerald-100">
                                <Check size={12} /> Save
                              </button>
                            ) : !q.locked && q.status !== "awarded" && (
                              <button onClick={() => startEditQuote(q)}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 rounded text-xs hover:bg-zinc-200">
                                <Pencil size={10} />
                              </button>
                            )}
                            {q.status !== "awarded" && (
                              <button onClick={() => toggleLockQuote(q.id, q.locked)}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-100 text-zinc-700 rounded text-xs hover:bg-zinc-200">
                                {q.locked ? <Unlock size={10} /> : <Lock size={10} />}
                              </button>
                            )}
                            {selected.status !== "awarded" && q.status !== "awarded" && (
                              <button onClick={() => awardQuotation(selected.id, q.id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-50 text-emerald-800 rounded-md text-xs font-medium hover:bg-emerald-100">
                                <Award size={12} /> Award
                              </button>
                            )}
                            {q.status === "awarded" && <span className="text-[10px] text-emerald-700 font-medium">Awarded</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-zinc-400 py-4">No quotations yet. Add one from a supplier.</p>
              )}

              {/* Comparison Matrix */}
              {comparison && comparison.matrix && comparison.matrix.length > 0 && (
                <div className="mt-6 border border-blue-200 rounded-lg p-4 bg-blue-50/30">
                  <h4 className="font-heading font-semibold text-sm mb-3 text-blue-900">Comparison Matrix</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-blue-200">
                          <th className="text-left py-2 text-[11px] uppercase tracking-[0.15em] text-blue-600 font-normal">Supplier</th>
                          <th className="text-right py-2 text-[11px] uppercase tracking-[0.15em] text-blue-600 font-normal">Unit Price</th>
                          <th className="text-right py-2 text-[11px] uppercase tracking-[0.15em] text-blue-600 font-normal">Lead Time</th>
                          <th className="text-right py-2 text-[11px] uppercase tracking-[0.15em] text-blue-600 font-normal">MOQ</th>
                          <th className="text-right py-2 text-[11px] uppercase tracking-[0.15em] text-blue-600 font-normal">Total Cost</th>
                          <th className="text-right py-2 text-[11px] uppercase tracking-[0.15em] text-blue-600 font-normal">Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {comparison.matrix.map((m, i) => (
                          <tr key={m.quotation_id} className={`border-b border-blue-100 ${i === 0 ? "bg-emerald-50" : ""}`}>
                            <td className="py-2.5 font-medium">{m.supplier_name}{i === 0 && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded-full">Best</span>}</td>
                            <td className="py-2.5 text-right">${m.unit_price.toFixed(2)}</td>
                            <td className="py-2.5 text-right">{m.lead_time_days}d</td>
                            <td className="py-2.5 text-right">{m.moq}</td>
                            <td className="py-2.5 text-right font-medium">${m.total_cost.toFixed(2)}</td>
                            <td className="py-2.5 text-right">
                              <span className={`font-semibold ${m.score >= 70 ? "text-emerald-700" : m.score >= 40 ? "text-amber-700" : "text-red-700"}`}>
                                {m.score}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-[10px] text-blue-500 mt-2">Scoring: 40% price + 30% lead time + 30% MOQ (higher is better)</p>
                </div>
              )}
            </div>
          )}

          {/* Split Award Modal */}
          {showSplitAward && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowSplitAward(false)}>
              <div className="bg-white rounded-lg p-8 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading font-semibold text-lg">Split Award</h2>
                  <button onClick={() => setShowSplitAward(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
                </div>
                <p className="text-xs text-zinc-500 mb-4">Allocate percentages across suppliers. Totals must equal 100%.</p>
                <div className="space-y-3">
                  {splitAwards.map((a, i) => (
                    <div key={a.quotation_id} className="grid grid-cols-12 gap-2 items-center">
                      <span className="col-span-5 text-sm font-medium truncate">{a.supplier_name}</span>
                      <div className="col-span-3">
                        <input type="number" min="0" max="100" step="0.01" value={a.percentage}
                          onChange={(e) => updateSplitAward(i, "percentage", e.target.value)}
                          className="w-full border border-zinc-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
                          placeholder="%" />
                      </div>
                      <div className="col-span-4">
                        <input type="number" min="0" value={a.quantity}
                          onChange={(e) => updateSplitAward(i, "quantity", e.target.value)}
                          className="w-full border border-zinc-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950"
                          placeholder="Qty" />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs text-zinc-500">
                    Total: {splitAwards.reduce((s, a) => s + (parseFloat(a.percentage) || 0), 0).toFixed(1)}%
                  </span>
                  <button onClick={submitSplitAward}
                    className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
                    <Award size={14} /> Apply Split Award
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Quotation Modal */}
          {showAddQuote && (
            <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowAddQuote(null)}>
              <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-heading font-semibold text-lg">Add Quotation</h2>
                  <button onClick={() => setShowAddQuote(null)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Supplier</label>
                    <select value={quoteForm.supplier_id} onChange={(e) => setQuoteForm({ ...quoteForm, supplier_id: e.target.value })}
                      className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                      <option value="">Select supplier...</option>
                      {suppliers.map((s) => <option key={s.id} value={s.id}>{s.company_name}</option>)}
                    </select>
                  </div>
                  <input value={quoteForm.price_tiers} onChange={(e) => setQuoteForm({ ...quoteForm, price_tiers: e.target.value })} placeholder="Unit prices (comma-separated)"
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                  <div className="grid grid-cols-2 gap-3">
                    <input type="number" value={quoteForm.moq} onChange={(e) => setQuoteForm({ ...quoteForm, moq: e.target.value })} placeholder="MOQ"
                      className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                    <input type="number" value={quoteForm.lead_time_weeks} onChange={(e) => setQuoteForm({ ...quoteForm, lead_time_weeks: e.target.value })} placeholder="Lead time (weeks)"
                      className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                  </div>
                  <input value={quoteForm.payment_terms} onChange={(e) => setQuoteForm({ ...quoteForm, payment_terms: e.target.value })} placeholder="Payment terms"
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                  <button onClick={() => addQuotation(showAddQuote)} disabled={!quoteForm.supplier_id}
                    className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                    Add Quotation
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New RFQ</h2>
              <button onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input value={form.style_number} onChange={(e) => setForm({ ...form, style_number: e.target.value })} placeholder="Style number"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Techpack (optional)</label>
                <select value={form.techpack_id} onChange={(e) => setForm({ ...form, techpack_id: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                  <option value="">Select techpack...</option>
                  {techpacks.map((t) => <option key={t.id} value={t.id}>{t.style_name || t.style_number || t.id.slice(0, 8)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Target quantity</label>
                  <input type="number" min="0" value={form.target_quantity} onChange={(e) => setForm({ ...form, target_quantity: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Currency</label>
                  <select value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-zinc-950">
                    {["USD", "EUR", "GBP", "INR", "CNY"].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Required lead time (weeks)</label>
                <input type="number" min="0" value={form.required_lead_time_weeks} onChange={(e) => setForm({ ...form, required_lead_time_weeks: e.target.value })}
                  placeholder="e.g. 8"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">BOM summary (comma-separated items)</label>
                <input value={form.bom_summary} onChange={(e) => setForm({ ...form, bom_summary: e.target.value })}
                  placeholder="e.g. Main fabric, Lining, Buttons, Zip"
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Response deadline</label>
                <input type="date" value={form.response_deadline} onChange={(e) => setForm({ ...form, response_deadline: e.target.value })}
                  className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              </div>
              <button onClick={create} disabled={!form.style_number || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating..." : "Create RFQ"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
